import { isTauri } from "./is-tauri";
import { translate } from "@/i18n";
import { libNoticesMessages } from "@/i18n/messages/lib-notices";

const DEFAULT_DOWNLOAD_PAGE_URL = "https://www.denker.ai/download";

export function getDesktopDownloadUrl(): string {
  // `||` (not `??`) so an empty-string override falls back to the default.
  // Docker compose's `${VAR:-}` expands an unset shell var to the empty
  // string, and Vite then inlines that empty string into the bundle —
  // `?? DEFAULT` would leave the call site returning "", which silently
  // breaks every download button (window.open("") is a no-op).
  return import.meta.env.VITE_DESKTOP_DOWNLOAD_URL || DEFAULT_DOWNLOAD_PAGE_URL;
}

// Direct link to the small Denker installer for in-place downloads on the
// auth pages: the browser saves the file without navigating away. Deploy
// resolves it from the channel's published installer index; without one the
// link is the download page, which resolves it live. Never the full app
// installer. Same `||` rationale as above for empty-string overrides.
export function getMacInstallerUrl(): string {
  return import.meta.env.VITE_MAC_INSTALLER_URL || getDesktopDownloadUrl();
}

/** Only a published Windows release exposes its installer URL. */
export function getWindowsInstallerUrl(): string {
  return windowsInstallerUrl() || getDesktopDownloadUrl();
}

/** Deploy resolves this only when the channel publishes a Windows release. */
function windowsInstallerUrl(): string {
  return (
    (import.meta.env.VITE_WINDOWS_INSTALLER_URL as string | undefined) || ""
  );
}

/** Direct installer for supported devices; Windows without a release goes
 *  to the download page with the macOS-only availability notice. */
export function getDesktopInstallerUrl(): string {
  return isWindowsOS() ? getWindowsInstallerUrl() : getMacInstallerUrl();
}

export function isMacOS(): boolean {
  if (typeof navigator === "undefined") return false;
  return /Mac/i.test(navigator.platform || navigator.userAgent);
}

export function isWindowsOS(): boolean {
  if (typeof navigator === "undefined") return false;
  const source = navigator.platform || navigator.userAgent;
  // Case-SENSITIVE, deliberately: /Win/i also matches "Darwin", which would
  // classify a Mac reporting that platform string as Windows. The landing
  // site's equivalent has always been case-sensitive for the same reason.
  return /Win/.test(source);
}

/**
 * Whether Denker publishes a Windows build on this server's channel.
 *
 * The handoff page shows a download to supported platforms and a "coming
 * soon" to everyone else, so this holds only when the deploy found a
 * published Windows installer: never a link to nothing.
 */
function isWindowsReleaseEnabled(): boolean {
  return windowsInstallerUrl() !== "";
}

export function desktopAvailabilityNotice(): string {
  return isWindowsReleaseEnabled()
    ? translate(libNoticesMessages, "desktopMacAndWindows")
    : translate(libNoticesMessages, "desktopMacOnly");
}

export function hasDesktopBuild(): boolean {
  return isMacOS() || (isWindowsReleaseEnabled() && isWindowsOS());
}

/** "Mac" or "Windows", for copy. Null where there is no build to name. */
export function desktopOsName(): "Mac" | "Windows" | null {
  if (isMacOS()) return "Mac";
  if (isWindowsReleaseEnabled() && isWindowsOS()) return "Windows";
  return null;
}

// The web canvas is desktop-only: every browser (non-Tauri) visitor is
// gated to /auth/desktop-handoff. Visitors on a platform we build for get
// open/download actions, everyone else a coming-soon notice — there is no
// browser escape hatch.
export function shouldShowDesktopPrompt(): boolean {
  return !isTauri();
}

const E2E_GATE_BYPASS_KEY = "denker.e2e-desktop-gate-bypass";

// Playwright-only escape so E2E suites can still exercise the web canvas
// (tests/e2e seeds this key at login). Never set by product UI; the gate
// is a UX redirect, not a security boundary, so a user flipping it in
// devtools gains nothing the API didn't already allow.
export function isDesktopGateBypassedForE2E(): boolean {
  try {
    return localStorage.getItem(E2E_GATE_BYPASS_KEY) === "1";
  } catch {
    return false;
  }
}
